/**
 * Topology-aware node layout, shared by the monitor canvas and the design
 * builder so a topology lands in the same place whichever you're looking at.
 */

export interface LayoutResult {
  positions: Map<string, { x: number; y: number }>;
  /** Flow direction — drives node handle sides so edges read naturally. */
  orientation: "vertical" | "horizontal";
}

/** Walks edges from an entry point to produce a linear ordering for pipelines. */
export function buildPipelineOrder(entryPoint: string, edges: any[]): string[] {
  const ordered: string[] = [entryPoint];
  const edgeMap = new Map<string, string>();
  for (const e of edges) {
    edgeMap.set(e.from, e.to);
  }
  let current = entryPoint;
  while (edgeMap.has(current)) {
    const next = edgeMap.get(current)!;
    if (ordered.includes(next)) break; // cycle guard
    ordered.push(next);
    current = next;
  }
  return ordered;
}

/**
 * Computes topology-aware node positions.
 * - pipeline: left-to-right in edge order
 * - star: entry point centered, others arranged radially
 * - default: hierarchical top-down (BFS depth levels from the entry point)
 */
export function layoutNodes(agentDefs: any[], def: any): LayoutResult {
  const positions = new Map<string, { x: number; y: number }>();
  const type = def.type;

  // Node footprint is ~256px wide; spacing must clear it plus a comfortable gap.
  const STEP_X = 360;
  const STEP_Y = 220;
  const centerX = 400;

  if (type === "pipeline") {
    const ordered = buildPipelineOrder(def.entryPoint, def.edges ?? []);
    for (const a of agentDefs) {
      if (!ordered.includes(a.id)) ordered.push(a.id);
    }
    ordered.forEach((id, i) => {
      positions.set(id, { x: 80 + i * STEP_X, y: 300 });
    });
    return { positions, orientation: "horizontal" };
  }

  if (type === "star") {
    const center = { x: centerX, y: 320 };
    const others = agentDefs.filter((a: any) => a.id !== def.entryPoint);
    positions.set(def.entryPoint, center);
    // Radius that keeps the center node and adjacent radial nodes from overlapping.
    const radius =
      others.length > 1
        ? Math.max(STEP_X, STEP_X / (2 * Math.sin(Math.PI / others.length)))
        : STEP_X;
    others.forEach((a: any, i: number) => {
      const angle = (2 * Math.PI * i) / others.length - Math.PI / 2;
      positions.set(a.id, {
        x: center.x + radius * Math.cos(angle),
        y: center.y + radius * Math.sin(angle),
      });
    });
    return { positions, orientation: "horizontal" };
  }

  // Default: hierarchical top-down. Assign depth levels by BFS from the entry
  // point along directed edges (parent -> child), so a delegation topology reads
  // top (entry) to bottom. Covers "hierarchical", "custom", and unknown types.
  const childrenOf = new Map<string, string[]>();
  for (const e of def.edges ?? []) {
    const list = childrenOf.get(e.from) ?? [];
    list.push(e.to);
    childrenOf.set(e.from, list);
  }

  const depth = new Map<string, number>();
  const queue: string[] = [];
  if (def.entryPoint) {
    depth.set(def.entryPoint, 0);
    queue.push(def.entryPoint);
  }
  while (queue.length > 0) {
    const node = queue.shift() as string;
    const d = depth.get(node) ?? 0;
    for (const child of childrenOf.get(node) ?? []) {
      if (!depth.has(child)) {
        depth.set(child, d + 1);
        queue.push(child);
      }
    }
  }

  // Nodes unreachable from the entry sit on a level below the deepest reached.
  let maxDepth = 0;
  for (const d of depth.values()) maxDepth = Math.max(maxDepth, d);
  const orphanDepth = depth.size > 0 ? maxDepth + 1 : 0;
  for (const a of agentDefs) {
    if (!depth.has(a.id)) depth.set(a.id, orphanDepth);
  }

  // Group nodes by level, then spread each level horizontally, centered.
  const levels = new Map<number, string[]>();
  for (const a of agentDefs) {
    const d = depth.get(a.id) ?? 0;
    const list = levels.get(d) ?? [];
    list.push(a.id);
    levels.set(d, list);
  }
  for (const [d, ids] of levels) {
    const totalW = (ids.length - 1) * STEP_X;
    ids.forEach((id, i) => {
      positions.set(id, { x: centerX - totalW / 2 + i * STEP_X, y: 80 + d * STEP_Y });
    });
  }

  return { positions, orientation: "vertical" };
}
